import { usePerms } from 'contexts/PermsContext';
import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSubmitFemaAlertMutation, useUpdateFemaAlertMutation } from 'services/ipaws/ipaws.api';
import {
  isSendGateResponse,
  normalizeSubmitResponse,
  processFemaSubmissionOutcome,
  validateFemaPreflightPayload,
} from 'services/ipaws/ipaws.service';
import type { FemaAlertPayload, FemaEnvironmentClass } from 'types/fema.types';
import {
  ALERT_CREATOR_ACCESS_LEVELS,
  FEMA_PERMISSIONS,
} from '../components/IpawsAlertFields/config/Ipaws.config';
import { selectIpawsHoldFlag } from '../state/selectors/alertCreatorSelectors';
import { setIpawsHoldFlag, setIpawsLastCapIdentifier } from '../state/slices/alertCreatorSlice';
import type { IpawsSubmissionStatusMessage } from '../utils/femaSubmissionResult.utils';
import {
  buildOutcomeResultMessage,
  buildSendGateResultMessage,
  buildSubmissionErrorMessage,
  buildUpdateResultMessage,
  INITIAL_SUBMISSION_MESSAGE,
} from '../utils/femaSubmissionResult.utils';
import type { AlertCreatorAlertData, IpawsPreviewModel } from '../utils/ipawsSubmission.utils';
import {
  getIpawsCombinedAudioFileError,
  getIpawsSpanishAudioBlockError,
} from '../utils/ipawsAudio.utils';
import {
  buildIpawsPreviewModel,
  buildIpawsSubmitPayload,
  validateIpawsSubmissionPayload,
} from '../utils/ipawsSubmission.utils';

/** The FEMA message a draft supersedes — set only while the operator authors an Update. */
export type IpawsUpdateTarget = { id: string | number; identifier: string };

export { readBackendError } from '../utils/femaSubmissionResult.utils';
export type {
  IpawsSendGateDetails,
  IpawsSubmissionStatusMessage,
} from '../utils/femaSubmissionResult.utils';

type UseIpawsSubmissionFlowArgs = {
  alertData: AlertCreatorAlertData;
  audioFile?: File | null;
  spanishAudioFile?: File | null;
  /** Problems already found by the form or map checks. */
  clientValidationIssues?: string[];
  /** Runs after a confirmed successful result so the caller can clear the draft. */
  onAcceptedSubmit?: () => void;
  /** Set to build an Update against this message instead of sending a new alert. */
  updateTarget?: IpawsUpdateTarget | null;
  /**
   * Multi-COG contract §1: the class that a NEW send carries as the multipart field
   * `environment`. `undefined` = send no class (an older backend). An Update never carries it.
   */
  environment?: FemaEnvironmentClass;
};

type UseIpawsSubmissionFlowResult = {
  previewModel: IpawsPreviewModel;
  isPreviewOpen: boolean;
  isSubmissionResultOpen: boolean;
  isSubmitting: boolean;
  statusMessage: IpawsSubmissionStatusMessage;
  submissionResultMessage: IpawsSubmissionStatusMessage;
  startPreview: () => Promise<void>;
  confirmAndSubmit: () => Promise<void>;
  closePreview: () => void;
  closeSubmissionResult: () => void;
};

const payloadIssuesToMessage = (issues: string[]): IpawsSubmissionStatusMessage => ({
  variant: 'danger',
  acceptanceMessage: 'Unable to submit.',
  text: issues.join(' '),
});

/**
 * Manages the IPAWS preview, final send, and result dialog.
 * It rebuilds and checks the alert before both Preview and Send so an old preview cannot bypass
 * changes made to the draft.
 * @param args - The alert, attached audio, permission context, and completion callback.
 * @returns The preview and submission state with their controls.
 */
export const useIpawsSubmissionFlow = ({
  alertData,
  audioFile,
  spanishAudioFile,
  clientValidationIssues = [],
  onAcceptedSubmit,
  updateTarget = null,
  environment,
}: UseIpawsSubmissionFlowArgs): UseIpawsSubmissionFlowResult => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSubmissionResultOpen, setIsSubmissionResultOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState<IpawsSubmissionStatusMessage>(
    INITIAL_SUBMISSION_MESSAGE,
  );
  const [submissionResultMessage, setSubmissionResultMessage] =
    useState<IpawsSubmissionStatusMessage>(INITIAL_SUBMISSION_MESSAGE);
  const [submitFemaAlert, { isLoading: isSubmittingAlert }] = useSubmitFemaAlertMutation();
  const [updateFemaAlert, { isLoading: isSubmittingUpdate }] = useUpdateFemaAlertMutation();
  const isSubmitting = updateTarget ? isSubmittingUpdate : isSubmittingAlert;
  const isSubmittingRef = useRef(false);
  const perms = usePerms();
  const ipawsHoldFlag = useSelector(selectIpawsHoldFlag);
  const dispatch = useDispatch();
  const previewModel = useMemo(() => buildIpawsPreviewModel(alertData), [alertData]);

  const getPayloadOrIssues = (): { payload: FemaAlertPayload; issues: string[] } => {
    const payload = buildIpawsSubmitPayload(alertData);

    // Form checks and final sent-data checks can report the same problem, so show it only once.
    const issues = Array.from(
      new Set([...clientValidationIssues, ...validateIpawsSubmissionPayload(payload)]),
    );
    const audioSizeError = getIpawsCombinedAudioFileError([audioFile, spanishAudioFile]);
    if (audioSizeError) issues.push(audioSizeError);
    // The backend puts the Spanish file in the es-US block and refuses it when that block is
    // missing; say so here, before the request.
    const spanishBlockError = getIpawsSpanishAudioBlockError(payload.info, spanishAudioFile);
    if (spanishBlockError) issues.push(spanishBlockError);

    // Keep the service's broad final check as a last guard for missing top-level fields.
    if (!validateFemaPreflightPayload(payload)) {
      issues.push('Submission payload is incomplete for FEMA API requirements.');
    }

    return { payload, issues };
  };

  const startPreview = async (): Promise<void> => {
    if (isSubmitting) return;
    // Belt and braces behind the hidden selector: the backend refuses without the permission
    // too (checkAccessPermsStrict — no admin bypass), so say so here rather than fail silently.
    // Update mode checks `manage` (a separate, strict key) instead of `create`.
    const requiredPermission = updateTarget ? FEMA_PERMISSIONS.manage : FEMA_PERMISSIONS.create;
    if (!perms.testPermission(ALERT_CREATOR_ACCESS_LEVELS, [requiredPermission])) {
      setIsPreviewOpen(false);
      setStatusMessage({
        variant: 'danger',
        acceptanceMessage: 'Permission denied.',
        text: updateTarget
          ? 'You do not have permission to update FEMA alerts.'
          : 'You do not have permission to create FEMA alerts.',
      });
      return;
    }
    const { issues } = getPayloadOrIssues();

    if (issues.length) {
      setIsPreviewOpen(false);
      setStatusMessage(payloadIssuesToMessage(issues));
      return;
    }

    setStatusMessage(INITIAL_SUBMISSION_MESSAGE);
    setIsPreviewOpen(true);
  };

  const confirmAndSubmit = async (): Promise<void> => {
    if (isSubmittingRef.current || isSubmitting || ipawsHoldFlag) return;
    const requiredPermission = updateTarget ? FEMA_PERMISSIONS.manage : FEMA_PERMISSIONS.create;
    if (!perms.testPermission(ALERT_CREATOR_ACCESS_LEVELS, [requiredPermission])) {
      setIsPreviewOpen(false);
      setStatusMessage({
        variant: 'danger',
        acceptanceMessage: 'Permission denied.',
        text: updateTarget
          ? 'You do not have permission to update FEMA alerts.'
          : 'You do not have permission to create FEMA alerts.',
      });
      return;
    }
    const { payload, issues } = getPayloadOrIssues();

    if (issues.length) {
      setStatusMessage(payloadIssuesToMessage(issues));
      return;
    }

    // The update route is JSON only — an EAS audio attachment has nowhere to go.
    if (updateTarget && (audioFile || spanishAudioFile)) {
      setStatusMessage({
        variant: 'danger',
        acceptanceMessage: 'Unable to submit.',
        text: 'An audio attachment cannot be sent with an Update. Remove it, or send a new alert.',
      });
      return;
    }

    // The hold (FE-03, decided 2026-08-26): raised before the request, released by whichever
    // lands first — this HTTP answer (in `finally`, so a 400 or a network error never leaves the
    // operator locked out) or the workplace's `ipaws-message-update` event.
    isSubmittingRef.current = true;
    dispatch(setIpawsHoldFlag(true));
    try {
      if (updateTarget) {
        // The route builds its own `<references>` from the target's stored bytes and refuses a
        // client-supplied one — never send whatever the draft carries.
        const { references: _references, ...updatePayload } = payload;
        const body = await updateFemaAlert({
          messageId: updateTarget.id,
          payload: { ...updatePayload, msgType: 'Update' },
        }).unwrap();
        setIsPreviewOpen(false);
        // `result.accepted` decides the outcome, never the HTTP status (buildUpdateResultMessage).
        setSubmissionResultMessage(buildUpdateResultMessage(body));
        setIsSubmissionResultOpen(true);
        return;
      }

      // The class goes beside `data`, never inside the CAP JSON (multi-COG contract §1).
      const body = await submitFemaAlert({
        payload,
        audioFile,
        spanishAudioFile,
        ...(environment ? { environment } : {}),
      }).unwrap();
      setIsPreviewOpen(false);
      const capIdentifier = (body as { capIdentifier?: unknown }).capIdentifier;
      if (typeof capIdentifier === 'string') dispatch(setIpawsLastCapIdentifier(capIdentifier));

      // The server may prepare and sign a message without sending it. Keep the draft and say so.
      if (isSendGateResponse(body)) {
        setSubmissionResultMessage(buildSendGateResultMessage(body));
        setIsSubmissionResultOpen(true);
        return;
      }

      const outcome = normalizeSubmitResponse(body);
      const processedResult = processFemaSubmissionOutcome(outcome);

      setSubmissionResultMessage(buildOutcomeResultMessage(outcome, processedResult));
      setIsSubmissionResultOpen(true);
    } catch (error) {
      // Server errors arrive here. Preserve the draft and show every field problem it returned.
      setIsPreviewOpen(false);
      setSubmissionResultMessage(
        buildSubmissionErrorMessage(error, 'Submit request failed.', 'Submit request failed.'),
      );
      setIsSubmissionResultOpen(true);
    } finally {
      isSubmittingRef.current = false;
      dispatch(setIpawsHoldFlag(false));
    }
  };

  const closeSubmissionResult = (): void => {
    // Clear the form only after a successful result. Failed or unsent messages stay editable.
    const shouldResetAfterResult =
      submissionResultMessage.variant !== 'danger' && !submissionResultMessage.keepDraft;

    setIsSubmissionResultOpen(false);

    if (shouldResetAfterResult) {
      setSubmissionResultMessage(INITIAL_SUBMISSION_MESSAGE);
      onAcceptedSubmit?.();
    }
  };

  return {
    previewModel,
    isPreviewOpen,
    isSubmissionResultOpen,
    isSubmitting,
    statusMessage,
    submissionResultMessage,
    startPreview,
    confirmAndSubmit,
    closePreview: () => setIsPreviewOpen(false),
    closeSubmissionResult,
  };
};

export default useIpawsSubmissionFlow;
